/**
 * Enum → translation-key maps.
 *
 * Every code the API returns is rendered through one of these maps, so a new
 * status can never reach the screen as a raw identifier and no component ever
 * hardcodes an English label. Keys are relative to the `wp` namespace.
 */

import type {
  ActionItemKind,
  AssetCondition,
  DocumentStatus,
  EmploymentStatus,
  EmploymentType,
  ExpenseStatus,
  LoanKind,
  PayFrequency,
  PayRunStatus,
  PayrollReportId,
  PerformanceRating,
  ProrationMethod,
  RoundingRule,
  UpdateRequestKind,
  UpdateRequestStatus,
} from '../data/types';
import type { LeaveStatus, LeaveType } from '../data/wagepro_store_leave';
import type { CompensationReason } from '../data/use_employee_compensation';
import type { StatusPillStatus } from '@jumpstone/ui-react';

export const EMPLOYMENT_STATUS_KEY: Record<EmploymentStatus, string> = {
  active: 'employment_status.active',
  on_leave: 'employment_status.on_leave',
  terminated: 'employment_status.terminated',
};

export const EMPLOYMENT_STATUS_TONE: Record<
  EmploymentStatus,
  'success' | 'warning' | 'danger'
> = {
  active: 'success',
  on_leave: 'warning',
  terminated: 'danger',
};

/** Why a salary entry exists -- the Reason dropdown and the history table. */
export const COMPENSATION_REASON_KEY: Record<CompensationReason, string> = {
  hire: 'employees.compensation_reason.hire',
  promotion: 'employees.compensation_reason.promotion',
  annual_review: 'employees.compensation_reason.annual_review',
  correction: 'employees.compensation_reason.correction',
  other: 'employees.compensation_reason.other',
};

export const EMPLOYMENT_TYPE_KEY: Record<EmploymentType, string> = {
  full_time: 'employment_type.full_time',
  part_time: 'employment_type.part_time',
  contract: 'employment_type.contract',
  contractor: 'employment_type.contractor',
};

/**
 * Badge tone per worker type. Deliberately never success/warning/danger —
 * a worker type is a category, not a condition (docs/design-system/CLAUDE.md
 * "semantic colour tokens used without the condition they signal").
 * Rendered ONLY by `screens/employees/employment_type_badge.tsx`.
 */
export const EMPLOYMENT_TYPE_TONE: Record<
  EmploymentType,
  'neutral' | 'accent'
> = {
  full_time: 'neutral',
  part_time: 'neutral',
  contract: 'neutral',
  contractor: 'accent',
};

export const PAY_FREQUENCY_KEY: Record<PayFrequency, string> = {
  monthly: 'pay_frequency.monthly',
  fortnightly: 'pay_frequency.fortnightly',
  weekly: 'pay_frequency.weekly',
};

export const DOCUMENT_STATUS_KEY: Record<DocumentStatus, string> = {
  active: 'document_status.active',
  expiring_soon: 'document_status.expiring_soon',
  archived: 'document_status.archived',
};

export const DOCUMENT_STATUS_TONE: Record<
  DocumentStatus,
  'success' | 'warning' | 'neutral'
> = {
  active: 'success',
  expiring_soon: 'warning',
  archived: 'neutral',
};

export const ASSET_CONDITION_KEY: Record<AssetCondition, string> = {
  good: 'asset_condition.good',
  fair: 'asset_condition.fair',
  poor: 'asset_condition.poor',
};

export const PERFORMANCE_RATING_KEY: Record<PerformanceRating, string> = {
  exceeds_expectations: 'performance_rating.exceeds_expectations',
  meets_expectations: 'performance_rating.meets_expectations',
  needs_improvement: 'performance_rating.needs_improvement',
};

export const LOAN_KIND_KEY: Record<LoanKind, string> = {
  salary_advance: 'loan_kind.salary_advance',
  staff_loan: 'loan_kind.staff_loan',
};

export const EXPENSE_STATUS_KEY: Record<ExpenseStatus, string> = {
  pending: 'expense_status.pending',
  approved: 'expense_status.approved',
  paid: 'expense_status.paid',
  rejected: 'expense_status.rejected',
};

// The leave-request status/type unions are owned by `data/wagepro_store_leave.ts`
// (the backend wire shape and the UI shape both live there). Its types are
// imported at the top of this file, alongside every other status/type union
// used by these enum→translation-key maps.
export const LEAVE_STATUS_KEY: Record<LeaveStatus, string> = {
  pending: 'leave_status.pending',
  approved: 'leave_status.approved',
  rejected: 'leave_status.rejected',
  cancelled: 'leave_status.cancelled',
};

/** Map a leave-request status onto the design system's accounting status pill. */
export const LEAVE_STATUS_PILL: Record<LeaveStatus, StatusPillStatus> = {
  pending: 'review',
  approved: 'approved',
  rejected: 'rejected',
  cancelled: 'voided',
};

export const LEAVE_TYPE_KEY: Record<LeaveType, string> = {
  annual: 'leave_type.annual',
  sick: 'leave_type.sick',
  unpaid: 'leave_type.unpaid',
  other: 'leave_type.other',
  casual: 'leave_type.casual',
};

export const PAY_RUN_STATUS_KEY: Record<PayRunStatus, string> = {
  draft: 'pay_run_status.draft',
  in_review: 'pay_run_status.in_review',
  approved: 'pay_run_status.approved',
  paid: 'pay_run_status.paid',
  filed: 'pay_run_status.filed',
  voided: 'pay_run_status.voided',
};

/** Map a pay-run status onto the design system's accounting status pill. */
export const PAY_RUN_STATUS_PILL: Record<PayRunStatus, StatusPillStatus> = {
  draft: 'draft',
  in_review: 'review',
  approved: 'approved',
  paid: 'paid',
  filed: 'sent',
  voided: 'voided',
};

export const UPDATE_REQUEST_KIND_KEY: Record<UpdateRequestKind, string> = {
  bank_account: 'update_request_kind.bank_account',
  address: 'update_request_kind.address',
  dependent: 'update_request_kind.dependent',
  emergency_contact: 'update_request_kind.emergency_contact',
  name_correction: 'update_request_kind.name_correction',
};

export const UPDATE_REQUEST_STATUS_KEY: Record<UpdateRequestStatus, string> = {
  in_review: 'update_request_status.in_review',
  approved: 'update_request_status.approved',
  rejected: 'update_request_status.rejected',
};

export const UPDATE_REQUEST_STATUS_PILL: Record<
  UpdateRequestStatus,
  StatusPillStatus
> = {
  in_review: 'review',
  approved: 'approved',
  // `rejected` is now a first-class StatusPillStatus; it used to alias onto
  // `draft`'s grey, which read a declined request as merely unfinished.
  rejected: 'rejected',
};

export const REPORT_NAME_KEY: Record<PayrollReportId, string> = {
  epf_form_c: 'reports.item.epf_form_c.name',
  etf_form_ii: 'reports.item.etf_form_ii.name',
  etf_form_r4: 'reports.item.etf_form_r4.name',
  apit_monthly: 'reports.item.apit_monthly.name',
  apit_annual: 'reports.item.apit_annual.name',
};

export const REPORT_DESCRIPTION_KEY: Record<PayrollReportId, string> = {
  epf_form_c: 'reports.item.epf_form_c.description',
  etf_form_ii: 'reports.item.etf_form_ii.description',
  etf_form_r4: 'reports.item.etf_form_r4.description',
  apit_monthly: 'reports.item.apit_monthly.description',
  apit_annual: 'reports.item.apit_annual.description',
};

export const ROUNDING_RULE_KEY: Record<RoundingRule, string> = {
  nearest_unit: 'rounding_rule.nearest_unit',
  nearest_ten: 'rounding_rule.nearest_ten',
};

export const PRORATION_METHOD_KEY: Record<ProrationMethod, string> = {
  calendar_day: 'proration_method.calendar_day',
  working_day: 'proration_method.working_day',
};

export const ACTION_ITEM_KEY: Record<ActionItemKind, string> = {
  expenses_pending: 'dashboard.action.expenses_pending',
  update_requests_open: 'dashboard.action.update_requests_open',
  permits_expiring: 'dashboard.action.permits_expiring',
  joiners_missing_bank_details: 'dashboard.action.joiners_missing_bank_details',
};

export const WIZARD_STEP_KEY = {
  period_scope: 'pay_run.step.period_scope',
  review_pay: 'pay_run.step.review_pay',
  statutory_summary: 'pay_run.step.statutory_summary',
  exceptions: 'pay_run.step.exceptions',
  approval: 'pay_run.step.approval',
  final_review: 'pay_run.step.final_review',
  post_run: 'pay_run.step.post_run',
} as const;
